import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, HStack, Stack, Button, useToast } from "@chakra-ui/react";
import {
  createNewArchiveRecord,
  getArchiveData,
  getResearchCentreArchiveDetailsAPI,
  updateArchiveData,
} from "../../../services/Archive";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import ImageWidget from "../../../CommonComponents/ImageWidget";
import { reverseTransformData, transformData } from "../../../util/helper";
import {
  archiveFactor,
  fetchArchiveDetails,
} from "../../../util/GetArchiveDetails";
import PreviewPage from "../../../components/PreviewPage";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import { getAllDocTypesData } from "../../../services/DocumentTypes";
import { getMasterListAliasSuggestions } from "../../../services/masterlistAlias";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import {
  handleApiError,
  showErrorToast,
  showSuccessToast,
} from "../../../util/errorHandler";

const Section1 = () => {
  const { alias } = useParams();
  const [masterList, setMasterList] = useState(null);
  const [artDetails, setArtDetails] = useState([]);
  const [castCrewData, setCastCrewData] = useState([]);
  const [filmData, setFilmData] = useState([]);
  const [producerData, setProducerData] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickSave, setQuickSave] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);

  const masterlistType = "film";
  const toast = useToast();
  const navigate = useNavigate();

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "isVisible",
      "alias",
      "masterlist_alias",
      "document_type",
      "document_type_in_full",
      "wnn",
      "auction_serial_no",
      "canto_cover_id",
      "image_alias",
      "direct_url_of_cover_image",
      "back_cover_image",
      "preview_back_cover_image",
      "video_link",
      "video_format",
      "duration",
    ];
    const rightFields = [
      "direct_url_of_preview_image",
      "preview_image",
      "image_alias_image",
      "cover_image",
      "content_image_thumbnails",
      "direct_url_of_content_images",
      "content_image_names",
      "canto_content_id",
      "canto_content_ids",
      "file_name",
      "can_enlarge",
      "corrected_sequence",
      "details_page",
      "pagination",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = (alias) => ({
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      isVisible: { type: "boolean", title: "Is Visible", default: false },
      alias: { type: "string", title: "Alias", default: "" },
      masterlist_alias: {
        type: "string",
        title: "Masterlist Alias",
        default: "",
      },
      document_type: { type: "string", title: "Document type", default: "" },
      document_type_in_full: {
        type: "string",
        title: "Document Type in Full",
        default: "",
      },
      ...(alias ? { wnn: { type: "string", title: "WNN", default: "" } } : {}),
      auction_serial_no: {
        type: "string",
        title: "Auction serial no",
        default: "",
      },
      canto_cover_id: { type: "string", title: "Canto cover id", default: "" },
      image_alias: { type: "string", title: "Image alias", default: "" },
      direct_url_of_cover_image: {
        type: "string",
        title: "Direct url of cover image",
        default: "",
      },
      back_cover_image: {
        type: "string",
        title: "Back cover image",
        default: "",
      },
      preview_back_cover_image: {
        type: "string",
        title: "Image URL",
        default: "",
      },
      video_link: { type: "string", title: "Video link", default: "" },
      video_format: { type: "string", title: "Video format", default: "" },
      duration: { type: "string", title: "Duration", default: "" },
    },
    required: ["alias", "masterlist_alias", "document_type"],
  });

  // Combined schema for preview mode
  const combinedSchema = (alias) => ({
    title: "Quadrant-1 fields",
    type: "object",
    properties: {
      _id: { type: "string" },
      isVisible: { type: "boolean", title: "Is Visible", default: false },
      alias: { type: "string", title: "Alias", default: "" },
      masterlist_alias: {
        type: "string",
        title: "Masterlist Alias",
        default: "",
      },
      document_type: { type: "string", title: "Document type", default: "" },
      document_type_in_full: {
        type: "string",
        title: "Document Type in Full",
        default: "",
      },
      ...(alias ? { wnn: { type: "string", title: "WNN", default: "" } } : {}),
      auction_serial_no: {
        type: "string",
        title: "Auction serial no",
        default: "",
      },
      direct_url_of_preview_image: {
        type: "string",
        title: "Direct url of preview image",
        default: "",
      },
      preview_image: { type: "string", title: "Image URL", default: "" },
      canto_cover_id: { type: "string", title: "Canto cover id", default: "" },
      image_alias: { type: "string", title: "Image alias", default: "" },
      image_alias_image: { type: "string", title: "Image URL", default: "" },
      direct_url_of_cover_image: {
        type: "string",
        title: "Direct url of cover image",
        default: "",
      },
      back_cover_image: {
        type: "string",
        title: "Back cover image",
        default: "",
      },
      preview_back_cover_image: {
        type: "string",
        title: "Image URL",
        default: "",
      },
      cover_image: { type: "string", title: "Image URL", default: "" },
      video_link: { type: "string", title: "Video link", default: "" },
      video_format: { type: "string", title: "Video format", default: "" },
      duration: { type: "string", title: "Duration", default: "" },
      content_image_thumbnails: {
        type: "array",
        title: "Content image thumbnails",
        items: {
          type: "object",
          properties: {
            image_alias: { type: "string", default: "" },
            image: { type: "string", default: "" },
          },
        },
      },
      direct_url_of_content_images: {
        type: "array",
        title: "Direct url of content images",
        items: {
          type: "object",
          properties: {
            image_alias: { type: "string", default: "" },
            image: { type: "string", default: "" },
          },
        },
      },
      content_image_names: {
        type: "array",
        items: { type: "string" },
        title: "Content image names",
      },
      canto_content_id: {
        type: "string",
        title: "Canto content id",
        default: "",
      },
      canto_content_ids: {
        type: "array",
        items: { type: "string" },
        title: "Canto content ids",
      },
      file_name: { type: "string", title: "FileName", default: "" },
      can_enlarge: { type: "boolean", title: "Can enlarge", default: false },
      corrected_sequence: {
        type: "string",
        title: "Corrected sequence",
        default: "",
      },
      details_page: { type: "string", title: "Details page", default: "" },
      pagination: {
        type: "string",
        title: "Precise Pagination in Publication",
        default: "",
      },
    },
    required: ["alias", "masterlist_alias", "document_type"],
  });

  // Left column UI schema
  const leftColumnUiSchema = (alias) => ({
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    alias: {
      "ui:readonly": !!alias,
      "ui:disabled": !!alias,
      "ui:emptyValue": "",
    },
    masterlist_alias: {
      "ui:emptyValue": "",
      "ui:widget": "masterlistAliasTypeahead",
    },
    document_type: { "ui:emptyValue": "", "ui:widget": "typeahead" },
    ...(alias
      ? {
          wnn: {
            "ui:emptyValue": "",
            "ui:readonly": true,
            "ui:disabled": true,
          },
        }
      : {}),
    document_type_in_full: { "ui:emptyValue": "" },
    auction_serial_no: { "ui:emptyValue": "" },
    canto_cover_id: { "ui:widget": "hidden" },
    image_alias: { "ui:emptyValue": "" },
    direct_url_of_cover_image: { "ui:emptyValue": "" },
    back_cover_image: { "ui:emptyValue": "" },
    preview_back_cover_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
    video_link: { "ui:widget": "hidden" },
    video_format: { "ui:widget": "hidden" },
    duration: { "ui:widget": "hidden" },
  });

  // Right column UI schema

  // Combined UI schema for preview mode
  const combinedUiSchema = (alias) => ({
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    alias: {
      "ui:readonly": !!alias,
      "ui:disabled": !!alias,
      "ui:emptyValue": "",
    },
    masterlist_alias: {
      "ui:emptyValue": "",
      "ui:widget": "masterlistAliasTypeahead",
    },
    document_type: { "ui:emptyValue": "", "ui:widget": "typeahead" },
    ...(alias
      ? {
          wnn: {
            "ui:emptyValue": "",
            "ui:readonly": true,
            "ui:disabled": true,
          },
        }
      : {}),
    document_type_in_full: { "ui:emptyValue": "" },
    auction_serial_no: { "ui:emptyValue": "" },
    direct_url_of_preview_image: { "ui:emptyValue": "" },
    preview_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
    canto_cover_id: { "ui:widget": "hidden" },
    image_alias: { "ui:emptyValue": "" },
    image_alias_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
    direct_url_of_cover_image: { "ui:emptyValue": "" },
    back_cover_image: { "ui:emptyValue": "" },
    preview_back_cover_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
    cover_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
    video_link: { "ui:widget": "hidden" },
    video_format: { "ui:widget": "hidden" },
    duration: { "ui:widget": "hidden" },
    content_image_thumbnails: {
      "ui:emptyValue": "",
      items: {
        image_alias: { "ui:emptyValue": "" },
        image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
      },
    },
    direct_url_of_content_images: {
      "ui:emptyValue": "",
      items: {
        image_alias: { "ui:emptyValue": "" },
        image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
      },
    },
    content_image_names: { "ui:emptyValue": "" },
    canto_content_id: { "ui:emptyValue": "" },
    canto_content_ids: { "ui:emptyValue": "" },
    file_name: { "ui:emptyValue": "", "ui:readonly": true },
  });

  // Fetch initial archive data
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!alias) return;
      setIsLoading(true);
      try {
        const result = await getArchiveData(alias, "section-1");

        if (result?.data?.[0]) {
          const fieldsToTransform = [
            "content_image_thumbnails",
            "direct_url_of_content_images",
          ];
          const transformedData = transformData(
            result?.data[0],
            fieldsToTransform,
          );
          transformedData.preview_back_cover_image =
            transformedData.back_cover_image;

          // Split data for two-column layout
          const { leftData, rightData } = splitFormData(transformedData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(transformedData);
          setMasterList(result.data[0].masterlist_alias);
          setQuickSave(false);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [alias, docTypes, toast, quickSave]);

  // Fetch additional details based on masterlist
  useEffect(() => {
    const fetchAdditionalDetails = async () => {
      if (!masterList) return;
      setIsLoading(true);
      try {
        if (masterlistType === "film") {
          await fetchArchiveDetails(
            masterList,
            setCastCrewData,
            setIsLoading,
            setFilmData,
            setProducerData,
          );
        }
        const archiveDetails = await getResearchCentreArchiveDetailsAPI(
          masterList,
          "search",
          alias,
        );
        if (archiveDetails?.data?.[0]?.archiveData) {
          setArtDetails(archiveFactor(archiveDetails.data[0].archiveData));
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch additional details",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdditionalDetails();
  }, [masterList, alias, toast, masterlistType]);

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      // reverseTransformData expects [{image_alias, image}] object arrays — only applies
      // to content_image_thumbnails + direct_url_of_content_images. content_image_names and
      // canto_content_ids are plain string arrays per the schema (type: array, items: string)
      // and were being silently emptied here (every string item maps to undefined.image_alias).
      const fieldsToTransform = [
        "content_image_thumbnails",
        "direct_url_of_content_images",
      ];
      const updatedFormData = reverseTransformData(
        finalFormData,
        fieldsToTransform,
      );

      if (alias) {
        const result = await updateArchiveData(alias, updatedFormData);
        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      } else {
        const result = await createNewArchiveRecord(updatedFormData);
        if (result?.status === 201) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "New archive record created successfully");
          navigate(`/archive/${result?.data?.alias}`);
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadDocTypes = async () => {
    try {
      const result = await getAllDocTypesData();
      setDocTypes(result?.data?.data || []);
      return result?.data?.data || [];
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to load document types",
      });
    }
  };

  const loadMasterlistAliasSuggestions = async (inputValue) => {
    try {
      const result = await getMasterListAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to load masterlist suggestions",
      });
    }
  };

  const fetchDocTypes = async (inputValue) => {
    const fetchedDocTypes = await loadDocTypes();

    if (!inputValue || typeof inputValue !== "string") {
      return fetchedDocTypes?.map((item) => ({
        label: item.alias,
        value: item.alias,
      }));
    }

    const searchValue = inputValue.trim().toLowerCase();
    const filteredResults =
      fetchedDocTypes &&
      fetchedDocTypes.filter((item) => {
        if (!item.alias) return false;
        return item.alias.toLowerCase()?.includes(searchValue);
      });

    return filteredResults?.map((item) => ({
      label: item.alias,
      value: item.alias,
    }));
  };

  const fetchMasterlistAliasSuggestions = async (inputValue) => {
    const fetchedMasterListAliasSuggestions =
      await loadMasterlistAliasSuggestions(inputValue);

    if (
      !inputValue ||
      typeof inputValue !== "string" ||
      !fetchedMasterListAliasSuggestions
    ) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias,
    }));
  };

  // Handle document type selection
  const handleSelectedDocType = useCallback(
    (selectedOption) => {
      if (!selectedOption) return;

      const docType = docTypes.find((item) => item.alias === selectedOption);
      if (!docType) {
        showErrorToast(toast, "Selected document type not found");
        return;
      }

      // Update both form data states
      const updateData = {
        document_type: selectedOption,
        document_type_in_full: docType.name || "",
      };

      setLeftFormData((prev) => ({ ...prev, ...updateData }));
      setCombinedFormData((prev) => ({ ...prev, ...updateData }));
    },
    [docTypes, toast],
  );

  // Memoized schemas
  const memoizedLeftSchema = useMemo(() => leftColumnSchema(alias), [alias]);
  const memoizedRightSchema = useMemo(
    () => ({
      title: "",
      type: "object",
      properties: {
        direct_url_of_preview_image: {
          type: "string",
          title: "Direct url of preview image",
          default: "",
        },
        preview_image: { type: "string", title: "Image URL", default: "" },
        image_alias_image: { type: "string", title: "Image URL", default: "" },
        cover_image: { type: "string", title: "Image URL", default: "" },
        content_image_thumbnails: {
          type: "array",
          title: "Content image thumbnails",
          items: {
            type: "object",
            properties: {
              image_alias: { type: "string", default: "" },
              image: { type: "string", default: "" },
            },
          },
        },
        direct_url_of_content_images: {
          type: "array",
          title: "Direct url of content images",
          items: {
            type: "object",
            properties: {
              image_alias: { type: "string", default: "" },
              image: { type: "string", default: "" },
            },
          },
        },
        content_image_names: {
          type: "array",
          items: { type: "string" },
          title: "Content image names",
        },
        canto_content_id: {
          type: "string",
          title: "Canto content id",
          default: "",
        },
        canto_content_ids: {
          type: "array",
          items: { type: "string" },
          title: "Canto content ids",
        },
        file_name: { type: "string", title: "FileName", default: "" },
        can_enlarge: { type: "boolean", title: "Can enlarge", default: false },
        corrected_sequence: {
          type: "string",
          title: "Corrected sequence",
          default: "",
        },
        details_page: { type: "string", title: "Details page", default: "" },
        pagination: {
          type: "string",
          title: "Precise Pagination in Publication",
          default: "",
        },
      },
    }),
    [],
  );
  const memoizedCombinedSchema = useMemo(() => combinedSchema(alias), [alias]);
  const memoizedLeftUISchema = useMemo(
    () => leftColumnUiSchema(alias),
    [alias],
  );
  const memoizedRightUISchema = useMemo(
    () => ({
      "ui:submitButtonOptions": { norender: true },
      "ui:style": {
        fontSize: "14px",
      },
      direct_url_of_preview_image: { "ui:emptyValue": "" },
      preview_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
      image_alias_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
      cover_image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
      content_image_thumbnails: {
        "ui:emptyValue": "",
        items: {
          image_alias: { "ui:emptyValue": "" },
          image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
        },
      },
      direct_url_of_content_images: {
        "ui:emptyValue": "",
        items: {
          image_alias: { "ui:emptyValue": "" },
          image: { "ui:widget": ImageWidget, "ui:emptyValue": "" },
        },
      },
      content_image_names: { "ui:emptyValue": "" },
      canto_content_ids: { "ui:emptyValue": "" },
      file_name: { "ui:emptyValue": "", "ui:readonly": true },
    }),
    [],
  );
  const memoizedCombinedUISchema = useMemo(
    () => combinedUiSchema(alias),
    [alias],
  );

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Archive Quadrant-1 Fields
                  </Box>
                </HStack>
                <Form
                  schema={memoizedCombinedSchema}
                  uiSchema={memoizedCombinedUISchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                  widgets={{
                    typeahead: (props) => (
                      <TypeaheadWidget
                        {...props}
                        fetchOptions={fetchDocTypes}
                        handleChangeCB={handleSelectedDocType}
                      />
                    ),
                    masterlistAliasTypeahead: (props) => (
                      <TypeaheadWidget
                        {...props}
                        fetchOptions={fetchMasterlistAliasSuggestions}
                      />
                    ),
                  }}
                />
              </Box>
              {/* PreviewPage component */}
              <HStack sx={{ position: "sticky", top: "91px" }}>
                <PreviewPage
                  artDetails={artDetails}
                  masterlistType={masterlistType}
                  castCrewData={castCrewData}
                  filmData={filmData}
                  producerData={producerData}
                  isLoading={isLoading}
                />
              </HStack>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Archive Quadrant-1 Fields
                      </Box>
                    </HStack>
                    <Form
                      schema={memoizedLeftSchema}
                      uiSchema={memoizedLeftUISchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                      widgets={{
                        typeahead: (props) => (
                          <TypeaheadWidget
                            {...props}
                            fetchOptions={fetchDocTypes}
                            handleChangeCB={handleSelectedDocType}
                          />
                        ),
                        masterlistAliasTypeahead: (props) => (
                          <TypeaheadWidget
                            {...props}
                            fetchOptions={fetchMasterlistAliasSuggestions}
                          />
                        ),
                      }}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={memoizedRightSchema}
                      uiSchema={memoizedRightUISchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* PreviewPage component */}
              <HStack sx={{ position: "sticky", top: "91px" }}>
                <PreviewPage
                  artDetails={artDetails}
                  masterlistType={masterlistType}
                  castCrewData={castCrewData}
                  filmData={filmData}
                  producerData={producerData}
                  isLoading={isLoading}
                />
              </HStack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section1;
